import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ClipboardList, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { ItemBinCountingRow, CountingListPagedParams } from '../../../../types/countingListCreation';
import { CreateCountingListModal } from './CreateCountingListModal';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../shared/table/types/table';
import { BackButton } from '../../../common/BackButton';

interface CountingListCreationProps {
    onBack: () => void;
}

export const CountingListCreation: React.FC<CountingListCreationProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const companyNo = user?.companyNo || '';

    // Server-side pagination state
    const [serverData, setServerData] = useState<ItemBinCountingRow[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Selection state
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Include zero stock toggle
    const [includeZeroStock, setIncludeZeroStock] = useState(false);

    // Fetch refs
    const isLoadingRef = useRef(false);
    const initialLoadDoneRef = useRef(false);
    const latestFetchParamsRef = useRef<CountingListPagedParams>({ pageNumber: 1, pageSize: 50 });
    const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Column factory
    const columnFactory = useMemo(() => createColumnFactory<ItemBinCountingRow>(), []);

    // Fetch paged data from server
    const fetchPagedData = useCallback(async (params: CountingListPagedParams) => {
        if (!companyNo) return;
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;

        if (!initialLoadDoneRef.current) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const result = await countingListCreationService.getItemBinsPaged({
                ...params,
                includeZeroStock
            });

            setServerData(result.data);
            setTotalRecords(result.totalRecords);
            latestFetchParamsRef.current = params;
            initialLoadDoneRef.current = true;
        } catch (err) {
            console.error('Error loading item bins:', err);
            setError(t('manager.modules.countingListCreation.messages.loadError', {}, 'Kunne ikke laste vare-/lokasjonsdata. Prøv igjen.'));
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [companyNo, includeZeroStock, t]);

    // Server-side pagination callback
    const handleServerFetch = useCallback((params: ServerFetchParams) => {
        const f = params.filters;
        const queryParams: CountingListPagedParams = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            sortColumn: params.sortColumn,
            sortDirection: params.sortDirection,
            itemNo: f?.itemNo || undefined,
            description1: f?.description1 || undefined,
            itemBarCode: f?.itemBarCode || undefined,
            supplierItemNo: f?.supplierItemNo || undefined,
            itemGrp1: f?.itemGrp1 || undefined,
            itemGrp2: f?.itemGrp2 || undefined,
            itemGrp3: f?.itemGrp3 || undefined,
            warehouseID: f?.warehouseID || undefined,
            binCode: f?.binCode || undefined,
            itemUnitCode: f?.itemUnitCode || undefined,
            stdLocationName: f?.stdLocationName || undefined,
            binQuantity: f?.binQuantity || undefined,
            lastCountedDate: f?.lastCountedDate || undefined,
            lastMovementDate: f?.lastMovementDate || undefined,
        };

        // Debounce filter changes
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }

        const prevFilters = latestFetchParamsRef.current;
        const filterKeys: (keyof CountingListPagedParams)[] = [
            'itemNo', 'description1', 'itemBarCode', 'supplierItemNo',
            'itemGrp1', 'itemGrp2', 'itemGrp3',
            'warehouseID', 'binCode', 'itemUnitCode', 'stdLocationName',
            'binQuantity', 'lastCountedDate', 'lastMovementDate'
        ];
        const isFilterChange = params.pageNumber === 1 &&
            filterKeys.some(key => queryParams[key] !== prevFilters[key]);

        if (isFilterChange) {
            filterDebounceRef.current = setTimeout(() => {
                fetchPagedData(queryParams);
            }, 300);
        } else {
            fetchPagedData(queryParams);
        }
    }, [fetchPagedData]);

    // Refresh helper
    const refreshData = useCallback(() => {
        fetchPagedData(latestFetchParamsRef.current);
    }, [fetchPagedData]);

    // Initial load
    useEffect(() => {
        fetchPagedData({ pageNumber: 1, pageSize: 50 });
    }, [fetchPagedData]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (filterDebounceRef.current) {
                clearTimeout(filterDebounceRef.current);
            }
        };
    }, []);

    // Re-fetch when zero stock toggle changes
    useEffect(() => {
        if (initialLoadDoneRef.current) {
            fetchPagedData({ ...latestFetchParamsRef.current, pageNumber: 1 });
        }
    }, [includeZeroStock, fetchPagedData]);

    // Row ID
    const getRowId = useCallback((row: ItemBinCountingRow) => row.rowKey, []);

    // Selection change
    const handleSelectionChange = useCallback((selectedIds: (string | number)[], _selectedData: ItemBinCountingRow[]) => {
        setSelectedRowKeys(selectedIds.map(id => String(id)));
    }, []);

    // Handle create button
    const handleCreateCountingList = useCallback(() => {
        if (selectedRowKeys.length === 0) {
            alert(t('manager.modules.countingListCreation.messages.selectAtLeastOne', {}, 'Velg minst én vare/lokasjon for å opprette telleliste'));
            return;
        }
        setShowCreateModal(true);
    }, [selectedRowKeys.length, t]);

    // Handle creation success
    const handleCreateSuccess = useCallback(() => {
        refreshData();
        setSelectedRowKeys([]);
        setShowCreateModal(false);
    }, [refreshData]);

    // Column definitions
    const columns: ColumnDefinition<ItemBinCountingRow>[] = useMemo(() => [
        columnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.countingListCreation.columns.itemNo', {}, 'Varenr.'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
            minWidth: 200,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('itemBarCode', 'itemBarCode', t('manager.modules.countingListCreation.columns.itemBarCode', {}, 'Strekkode'), {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
            minWidth: 80,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createNumberColumn('binQuantity', 'binQuantity', t('manager.modules.countingListCreation.columns.binQuantity', {}, 'Beholdning'), {
            minWidth: 100,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('supplierItemNo', 'supplierItemNo', t('manager.modules.countingListCreation.columns.supplierItemNo', {}, 'Lev.varenr.'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', t('manager.modules.countingListCreation.columns.itemUnitCode', {}, 'Enhet'), {
            minWidth: 70,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemGrp1', 'itemGrp1', t('manager.modules.countingListCreation.columns.itemGrp1', {}, 'Varegruppe 1'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemGrp2', 'itemGrp2', t('manager.modules.countingListCreation.columns.itemGrp2', {}, 'Varegruppe 2'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemGrp3', 'itemGrp3', t('manager.modules.countingListCreation.columns.itemGrp3', {}, 'Varegruppe 3'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('stdLocationName', 'stdLocationName', t('manager.modules.countingListCreation.columns.stdLocationName', {}, 'Std.lokasjon'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', t('manager.modules.countingListCreation.columns.lastCountedDate', {}, 'Sist telt'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', t('manager.modules.countingListCreation.columns.lastMovementDate', {}, 'Sist bevegelse'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
    ], [columnFactory, t]);

    // Table actions
    const tableActions: TableAction<ItemBinCountingRow>[] = useMemo(() => [
        {
            id: 'create-counting-list',
            label: t('manager.modules.countingListCreation.actions.createCountList', { '0': selectedRowKeys.length }, `Opprett telleliste (${selectedRowKeys.length})`),
            icon: ClipboardList,
            onClick: () => handleCreateCountingList(),
            variant: 'primary' as const,
            disabled: () => selectedRowKeys.length === 0
        },
        {
            id: 'refresh',
            label: t('common.buttons.refresh', {}, 'Oppdater'),
            icon: RefreshCw,
            onClick: () => refreshData(),
            variant: 'secondary' as const
        }
    ], [selectedRowKeys.length, handleCreateCountingList, refreshData, t]);

    // Zero stock toggle as toolbar element with active styling
    const zeroStockToggle = useMemo(() => (
        <button
            onClick={() => setIncludeZeroStock(prev => !prev)}
            className={`px-3 py-2 text-xs font-semibold rounded-lg border flex items-center gap-1.5 transition-all duration-200 ${
                includeZeroStock
                    ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700'
                    : 'text-blue-700 bg-white border-blue-300 hover:bg-blue-50 hover:shadow-md'
            }`}
            title={includeZeroStock
                ? t('manager.modules.countingListCreation.toggle.hideZeroStock', {}, 'Skjul lokasjoner med 0 beholdning')
                : t('manager.modules.countingListCreation.toggle.showZeroStock', {}, 'Vis lokasjoner med 0 beholdning')}
        >
            {includeZeroStock ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            {t('manager.modules.countingListCreation.toggle.includeZeroStock', {}, 'Inkluder nullbeholdning')}
        </button>
    ), [includeZeroStock, t]);

    // Get selected rows data for the modal
    const selectedRowsData = useMemo(() =>
        serverData.filter(row => selectedRowKeys.includes(row.rowKey)),
        [serverData, selectedRowKeys]
    );

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-50">
            {/* Sub-header with back button and page title */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-300">
                <div className="px-4 py-2 flex items-center gap-2">
                    <BackButton onClick={onBack} />
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    <h1 className="text-lg font-semibold text-gray-900">{t('manager.modules.countingListCreation.page.title', {}, 'Opprett tellelister')}</h1>
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <WarehouseTable<ItemBinCountingRow>
                    data={serverData}
                    columns={columns}
                    config={{
                        showPagination: true,
                        showColumnConfig: true,
                        showGlobalSearch: false,
                        showFilters: true,
                        showExport: false,
                        showPrint: false,
                        showSummary: false,
                        defaultPageSize: 50,
                        allowColumnReorder: true,
                        allowColumnResize: true,
                        stickyHeader: true,
                        compactMode: true,
                        persistenceKey: 'counting-list-creation-table',
                        allowRowSelection: {
                            mode: 'multi',
                            selectAllMode: 'current-page'
                        }
                    }}
                    serverPagination={{
                        enabled: true,
                        totalRecords: totalRecords,
                        onFetchData: handleServerFetch
                    }}
                    selectedRows={selectedRowKeys}
                    onSelectionChange={handleSelectionChange}
                    getRowId={getRowId}
                    tableActions={tableActions}
                    toolbarLeft={zeroStockToggle}
                    loading={isLoading}
                    error={error || undefined}
                />
            </div>

            {/* Create Counting List Modal */}
            {showCreateModal && selectedRowKeys.length > 0 && (
                <CreateCountingListModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    selectedRows={selectedRowsData}
                    onSuccess={handleCreateSuccess}
                />
            )}
        </div>
    );
};

export default CountingListCreation;
